import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { bool, func, number, object, string } from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';

import { Form, RangeSlider } from '../../../components';

import css from './PriceFilterForm.module.css';

const DEBOUNCE_WAIT_TIME = 400;

const digitsOnly = value => `${value}`.replace(/[^\d]/g, '');

// Helper function to parse value for min handle
// Value needs to be between slider's minimum value and current maximum value
const normalizeMin = (min, currentMax) => value => {
  const sanitizedValue = digitsOnly(value);
  if (sanitizedValue === '') {
    return '';
  }
  const parsedValue = Number.parseInt(sanitizedValue, 10);
  if (isNaN(parsedValue)) {
    return '';
  }
  return parsedValue < min ? min : parsedValue > currentMax ? currentMax : parsedValue;
};

// Helper function to parse value for max handle
// Value needs to be between slider's max value and current minimum value
const normalizeMax = (max, currentMin) => value => {
  const sanitizedValue = digitsOnly(value);
  if (sanitizedValue === '') {
    return '';
  }
  const parsedValue = Number.parseInt(sanitizedValue, 10);
  if (isNaN(parsedValue)) {
    return '';
  }
  return parsedValue < currentMin ? currentMin : parsedValue > max ? max : parsedValue;
};

const resolvePrice = (value, fallback, lowerBound, upperBound) => {
  if (value === '' || value == null) {
    return fallback;
  }
  const sanitizedValue = digitsOnly(value);
  if (sanitizedValue === '') {
    return fallback;
  }
  const parsedValue = Number.parseInt(sanitizedValue, 10);
  if (isNaN(parsedValue)) {
    return fallback;
  }
  return parsedValue < lowerBound ? lowerBound : parsedValue > upperBound ? upperBound : parsedValue;
};

// PriceFilterForm component
const PriceFilterFormComponent = props => {
  const { liveEdit, onChange, onSubmit, onCancel, onClear, min: minBound, max: maxBound, ...rest } = props;
  const isDraggingRef = useRef(false);

  if (liveEdit && !onChange) {
    throw new Error('PriceFilterForm: if liveEdit is true you need to provide onChange function');
  }

  if (!liveEdit && !(onCancel && onClear && onSubmit)) {
    throw new Error(
      'PriceFilterForm: if liveEdit is false you need to provide onCancel, onClear, and onSubmit functions'
    );
  }

  const emitLiveChange = useCallback(
    (values, min, max) => {
      if (!onChange) {
        return;
      }
      const { minPrice, maxPrice, ...restValues } = values;
      const resolvedMax = resolvePrice(maxPrice, max, min, max);
      const resolvedMin = resolvePrice(minPrice, min, min, resolvedMax);
      if (resolvedMin !== min || resolvedMax !== max) {
        onChange({
          minPrice: resolvedMin,
          maxPrice: resolvedMax,
          ...restValues,
        });
      }
    },
    [onChange]
  );

  const handleChange = useMemo(
    () =>
      debounce(
        formState => {
          if (isDraggingRef.current) {
            return;
          }
          emitLiveChange(formState.values, minBound, maxBound);
        },
        DEBOUNCE_WAIT_TIME,
        { leading: false, trailing: true }
      ),
    [emitLiveChange, maxBound, minBound]
  );

  useEffect(() => {
    return () => {
      handleChange.cancel();
    };
  }, [handleChange]);

  const handleSubmit = values => {
    const { minPrice, maxPrice, ...restValues } = values;
    const resolvedMax = resolvePrice(maxPrice, maxBound, minBound, maxBound);
    const resolvedMin = resolvePrice(minPrice, minBound, minBound, resolvedMax);
    return onSubmit({
      minPrice: resolvedMin,
      maxPrice: resolvedMax,
      ...restValues,
    });
  };

  const formCallbacks = liveEdit
    ? { onSubmit: () => null }
    : { onSubmit: handleSubmit, onCancel, onClear };
  return (
    <FinalForm
      {...rest}
      min={minBound}
      max={maxBound}
      {...formCallbacks}
      keepDirtyOnReinitialize
      render={formRenderProps => {
        const {
          form,
          handleSubmit,
          id,
          showAsPopup,
          onClear,
          onCancel,
          isOpen,
          isInSideBar,
          contentRef,
          style,
          intl,
          values,
          min,
          max,
          step,
          children,
        } = formRenderProps;
        const { minPrice: minPriceRaw, maxPrice: maxPriceRaw } = values;
        const maxPrice = resolvePrice(maxPriceRaw, max, min, max);
        const minPrice = resolvePrice(minPriceRaw, min, min, maxPrice);

        const handleCancel = () => {
          // reset the final form to initialValues
          form.reset();
          onCancel();
        };

        const clear = intl.formatMessage({ id: 'PriceFilterForm.clear' });
        const cancel = intl.formatMessage({ id: 'PriceFilterForm.cancel' });
        const submit = intl.formatMessage({ id: 'PriceFilterForm.submit' });

        const classes = classNames(css.root, {
          [css.popup]: showAsPopup,
          [css.isOpenAsPopup]: showAsPopup && isOpen,
          [css.plain]: !showAsPopup,
          [css.isOpen]: !showAsPopup && isOpen,
        });

        const handleMinBlur = () => {
          form.change('minPrice', normalizeMin(min, maxPrice)(form.getState().values.minPrice));
        };

        const handleMaxBlur = () => {
          form.change('maxPrice', normalizeMax(max, minPrice)(form.getState().values.maxPrice));
        };

        return (
          <Form
            className={classes}
            onSubmit={handleSubmit}
            tabIndex="0"
            contentRef={contentRef}
            style={style}
          >
            {liveEdit ? (
              <FormSpy onChange={handleChange} subscription={{ values: true, dirty: true }} />
            ) : (
              <div className={css.buttonsWrapper}>
                <button className={css.clearButton} type="button" onClick={onClear}>
                  {clear}
                </button>
                <button className={css.cancelButton} type="button" onClick={handleCancel}>
                  {cancel}
                </button>
                <button className={css.submitButton} type="submit">
                  {submit}
                </button>
              </div>
            )}

            <div className={css.inputsRow}>
              <Field name="minPrice">
                {fieldRenderProps => {
                  const { input } = fieldRenderProps;
                  return (
                    <label className={css.inputGroup} htmlFor={`${id}.minPrice`}>
                      <span className={css.inputLabel}>
                        <FormattedMessage id="PriceFilterForm.minLabel" defaultMessage="Min" />
                      </span>
                      <span className={css.inputShell}>
                        <span className={css.currencyPrefix}>$</span>
                        <input
                          {...input}
                          id={`${id}.minPrice`}
                          className={css.priceInput}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          onChange={e => {
                            input.onChange(digitsOnly(e.target.value));
                          }}
                          onBlur={e => {
                            input.onBlur(e);
                            handleMinBlur();
                          }}
                        />
                      </span>
                    </label>
                  );
                }}
              </Field>

              <Field name="maxPrice">
                {fieldRenderProps => {
                  const { input } = fieldRenderProps;
                  return (
                    <label className={css.inputGroup} htmlFor={`${id}.maxPrice`}>
                      <span className={css.inputLabel}>
                        <FormattedMessage id="PriceFilterForm.maxLabel" defaultMessage="Max" />
                      </span>
                      <span className={css.inputShell}>
                        <span className={css.currencyPrefix}>$</span>
                        <input
                          {...input}
                          id={`${id}.maxPrice`}
                          className={css.priceInput}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          onChange={e => {
                            input.onChange(digitsOnly(e.target.value));
                          }}
                          onBlur={e => {
                            input.onBlur(e);
                            handleMaxBlur();
                          }}
                        />
                      </span>
                    </label>
                  );
                }}
              </Field>
            </div>

            <div className={css.sliderWrapper}>
              <RangeSlider
                key={`price-slider-${isOpen ? 'open' : 'closed'}`}
                min={min}
                max={max}
                step={step}
                handles={[minPrice, maxPrice]}
                onChange={handles => {
                  form.batch(() => {
                    form.change('minPrice', handles[0]);
                    form.change('maxPrice', handles[1]);
                  });
                }}
                onDragStart={() => {
                  isDraggingRef.current = true;
                  handleChange.cancel();
                }}
                onDragEnd={() => {
                  isDraggingRef.current = false;
                  handleChange.cancel();
                  emitLiveChange(form.getState().values, min, max);
                }}
              />
            </div>

            <p className={css.hint}>Type exact values or drag the slider</p>

            {children}
          </Form>
        );
      }}
    />
  );
};

PriceFilterFormComponent.defaultProps = {
  liveEdit: false,
  showAsPopup: false,
  isOpen: false,
  contentRef: null,
  style: null,
  min: 0,
  step: 1,
  onCancel: null,
  onChange: null,
  onClear: null,
  onSubmit: null,
};

PriceFilterFormComponent.propTypes = {
  id: string.isRequired,
  liveEdit: bool,
  showAsPopup: bool,
  onCancel: func,
  onChange: func,
  onClear: func,
  onSubmit: func,
  isOpen: bool,
  contentRef: func,
  style: object,
  min: number.isRequired,
  max: number.isRequired,
  step: number,

  // form injectIntl
  intl: intlShape.isRequired,
};

const PriceFilterForm = injectIntl(PriceFilterFormComponent);

export default PriceFilterForm;
